'use client'

import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Suspense } from 'react'

import { buildLeaderboardPath } from '@/features/leaderboard/leaderboardUrl'
import { useRouter } from '@/i18n/routing'
import { isAthleteEnabled } from '@/lib/auth/athleteFeature'
import BackToTop from '@/ui/BackToTop'

import SalesBanner from '../news/components/SalesBanner'
import FansClubsToggle, {
  type LeaderboardAudience
} from './components/FansClubsToggle'
import GlobalAthletesLeaderboard from './components/GlobalAthletesLeaderboard'
import GlobalClubsLeaderboard from './components/GlobalClubsLeaderboard'
import GlobalFansLeaderboard from './components/GlobalFansLeaderboard'
import LeaderboardBackground from './components/LeaderboardBackground'
import S from './LeaderboardPage.module.scss'

function readLeaderboardAudience(
  searchParams: ReturnType<typeof useSearchParams> | null
): LeaderboardAudience {
  const audience = searchParams?.get('audience')
  if (audience === 'clubs') {
    return 'clubs'
  }
  // BAT-1110: «Athletes» only with the athlete feature on.
  return audience === 'athletes' && isAthleteEnabled() ? 'athletes' : 'fans'
}

const LeaderboardPageContent = () => {
  const t = useTranslations('leaderboard')
  const tb = useTranslations('leaderboardBoard')
  const router = useRouter()
  const audience = readLeaderboardAudience(useSearchParams())

  const setAudience = (next: LeaderboardAudience) =>
    router.replace(buildLeaderboardPath({ audience: next }), { scroll: false })

  return (
    <div className={S.leaderboardPage}>
      <LeaderboardBackground title={t('title.globalLeaderboard')}>
        <FansClubsToggle
          value={audience}
          onChange={setAudience}
          withAthletes={isAthleteEnabled()}
        />
      </LeaderboardBackground>
      <div className={S.tableContainer}>
        {audience === 'fans' ? (
          <GlobalFansLeaderboard />
        ) : audience === 'clubs' ? (
          <GlobalClubsLeaderboard />
        ) : (
          <GlobalAthletesLeaderboard />
        )}
      </div>
      <SalesBanner />
      <BackToTop label={tb('backToTop')} />
    </div>
  )
}

const LeaderboardPage = () => (
  <Suspense fallback={null}>
    <LeaderboardPageContent />
  </Suspense>
)

export default LeaderboardPage
